import React from 'react'
import {
    Box,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Alert,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import CloseIcon from '@mui/icons-material/Close'
import styled from 'styled-components'
import {getCurrentColors} from '../../../../utils/colors'
import {Project} from '../../../../types/getDocuments'
import {useNavigate} from 'react-router-dom'

const COLORS = getCurrentColors()

interface ResumeItem {
    id: string
    question_id: string
    question_title: string
    content: {
        text: string
        table: Array<Array<{[key: string]: any}>>
        options?: string[]
    }
}

interface DocumentResumeProps {
    projectName: string
    resume: ResumeItem[]
    showAlert: boolean
    generatingProject: boolean
    onEditQuestion: (questionId: string) => void
    onFinishProject: () => void
    documents: Project | undefined
    onlyView?: boolean
}

const formatCurrency = (value: number): string => {
    return value.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })
}

const COLUMN_TRANSLATIONS = {
    name: 'Nome',
    quantity: 'Quantidade',
    price: 'Preço',
    total_price: 'Preço Total',
    description: 'Descrição',
    unity: 'Unidade',
    bool: 'Possui',
}

const translateColumn = (key: string): string => {
    return COLUMN_TRANSLATIONS[key as keyof typeof COLUMN_TRANSLATIONS] || key
}

const isCurrencyField = (key: string): boolean => {
    const lowerKey = key.toLowerCase()
    return (
        lowerKey.includes('preço') ||
        lowerKey.includes('preco') ||
        lowerKey.includes('total')
    )
}

const Footer = styled.div`
    height: 57px;
    width: 100%;
    background-color: #ffffff;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 16px;
    position: absolute;
    bottom: 0;
    left: 0;
`

const GradientButton = styled.button`
    background: linear-gradient(90deg, #1db8e8 0%, #26e87d 100%);
    border: none;
    color: white;
    padding: 8px 16px;
    font-size: 14px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        opacity: 0.9;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`

const Page = styled.div`
    height: 100vh;
    margin-top: 0;
    padding: 64px 48px 48px;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow-y: auto;
    background-color: #f6f8fa;
    flex: 1;
`

const Header = styled.div`
    width: 100%;
    margin-bottom: 32px;
`

const ContentContainer = styled.div`
    width: 70%;
    max-width: 1000px;
    background-color: #ffffff;
    padding: 32px;
    border-radius: 10px;
    margin-bottom: 24px;
    align-self: center;
    overflow-y: auto;
`

export const DocumentResume: React.FC<DocumentResumeProps> = ({
    projectName,
    resume,
    showAlert,
    generatingProject,
    onEditQuestion,
    onFinishProject,
    documents,
    onlyView = false,
}) => {
    const navigate = useNavigate()
    React.useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Enter') return

            if (showAlert || generatingProject) return

            const active = document.activeElement as HTMLElement | null
            const tag = active?.tagName

            if (
                tag === 'INPUT' ||
                tag === 'TEXTAREA' ||
                active?.isContentEditable
            )
                return

            e.preventDefault()
            onFinishProject()
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [showAlert, generatingProject, onFinishProject])
    return (
        <Page>
            <Header>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                    }}
                >
                    <Box>
                        <Typography variant="h4">{projectName}</Typography>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 400,
                                fontSize: '18px',
                                color: '#707070',
                                marginTop: '8px',
                            }}
                        >
                            Revise as informações do seu questionário antes de
                            gerar os documentos
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            cursor: 'pointer',
                            padding: '8px',
                            '&:hover': {
                                backgroundColor: '#f5f5f5',
                                borderRadius: '50%',
                            },
                        }}
                        onClick={() => navigate('/projetos')}
                    >
                        <CloseIcon sx={{fontSize: '24px', color: '#707070'}} />
                    </Box>
                </Box>
                {showAlert && (
                    <Alert severity="error">
                        Os seus créditos de Documentos acabaram.
                    </Alert>
                )}
            </Header>

            <ContentContainer>
                {documents && documents.documents.length > 0 && !onlyView && (
                    <Box sx={{marginBottom: 3}}>
                        <Typography
                            variant="h6"
                            sx={{marginBottom: 2, fontWeight: 'bold'}}
                        >
                            Documentos que serão gerados:
                        </Typography>
                        {documents.documents
                            .filter((doc) => doc.status === 'INCOMPLETE')
                            .map((doc, index) => (
                                <Typography
                                    key={index}
                                    variant="body1"
                                    sx={{
                                        marginBottom: 1,
                                        '&:before': {
                                            content: '"•"',
                                            marginRight: 1,
                                            color: 'white',
                                        },
                                    }}
                                >
                                    {doc.name.split(' - ')[0]}
                                </Typography>
                            ))}
                    </Box>
                )}
                <Typography
                    variant="h6"
                    sx={{marginBottom: 2, fontWeight: 'bold'}}
                >
                    Resumo das Respostas:
                </Typography>
                {resume.map((item, index) => (
                    <Box key={index} sx={{marginBottom: 2}}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'gray',
                                fontSize: '0.75rem',
                                marginBottom: '4px',
                            }}
                        >
                            Pergunta {index + 1} de {resume.length}
                        </Typography>

                        <Box
                            {...(!onlyView && {
                                onClick: () => onEditQuestion(item.question_id),
                            })}
                            {...(!onlyView && {style: {cursor: 'pointer'}})}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                ...(!onlyView && {
                                    '&:hover': {
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: '4px',
                                        padding: '4px',
                                    },
                                }),
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                sx={{fontWeight: 'bold'}}
                            >
                                {item.question_title}
                            </Typography>
                            {!onlyView && (
                                <EditIcon
                                    sx={{fontSize: '1rem', color: 'gray'}}
                                />
                            )}
                        </Box>

                        {item.content.text && (
                            <Typography
                                variant="body2"
                                sx={{color: 'gray', mt: 1}}
                            >
                                {item.content.text}
                            </Typography>
                        )}

                        {item.content.options &&
                            Array.isArray(item.content.options) &&
                            item.content.options.length > 0 && (
                                <Box sx={{mt: 1}}>
                                    {item.content.options.map(
                                        (option, optionIndex) => (
                                            <Typography
                                                key={optionIndex}
                                                variant="body2"
                                                sx={{color: 'gray'}}
                                            >
                                                • {option}
                                            </Typography>
                                        )
                                    )}
                                </Box>
                            )}

                        {item.content.table &&
                            item.content.table.length > 0 && (
                                <>
                                    {item.content.table.map((table, idx) => (
                                        <Table
                                            key={idx}
                                            style={{
                                                marginBottom: '16px',
                                                border: '1px solid #ccc',
                                            }}
                                            sx={{minWidth: 650}}
                                            aria-label="resume table"
                                        >
                                            <TableHead>
                                                <TableRow>
                                                    {Object.keys(
                                                        table[0] || {}
                                                    ).map((key, index) => (
                                                        <TableCell key={index}>
                                                            {translateColumn(
                                                                key
                                                            )}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {table.map((row, rowIndex) => (
                                                    <TableRow key={rowIndex}>
                                                        {Object.keys(
                                                            table[0] || {}
                                                        ).map(
                                                            (
                                                                key,
                                                                cellIndex
                                                            ) => (
                                                                <TableCell
                                                                    key={
                                                                        cellIndex
                                                                    }
                                                                >
                                                                    {isCurrencyField(
                                                                        key
                                                                    )
                                                                        ? formatCurrency(
                                                                              parseFloat(
                                                                                  row[
                                                                                      key
                                                                                  ]
                                                                              ) ||
                                                                                  0
                                                                          )
                                                                        : row[
                                                                              key
                                                                          ]}
                                                                </TableCell>
                                                            )
                                                        )}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    ))}
                                </>
                            )}
                    </Box>
                ))}
            </ContentContainer>
            {!onlyView && (
                <Footer>
                    <Typography
                        style={{marginLeft: '10%', fontSize: '18px'}}
                        variant="body2"
                    >
                        Tudo certo com seu documento?
                    </Typography>
                    <GradientButton
                        disabled={showAlert || generatingProject}
                        onClick={onFinishProject}
                        style={{marginRight: '10%'}}
                    >
                        Gerar Documento
                    </GradientButton>
                </Footer>
            )}
        </Page>
    )
}
